import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Upload, CheckCircle, XCircle, Loader2, FileSpreadsheet, Brain, RefreshCw, FileText, AlertTriangle, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

/* ─── Butler21 Excel Helpers ─── */
function parseDatum(s) {
  if (!s) return null;
  const str = String(s).trim();
  const m = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) return m[3]+'-'+m[2].padStart(2,'0')+'-'+m[1].padStart(2,'0');
  return str;
}
function parseG(s) {
  const v = String(s||'').toLowerCase();
  if (v.match(/^(m|männl|herr)/)) return 'm';
  if (v.match(/^(w|f|weibl|frau)/)) return 'w';
  return 'd';
}
function splitStr(a) {
  const m = String(a||'').trim().match(/^(.+?)\s+(\d+\w*)$/);
  return m ? [m[1].trim(),m[2].trim()] : [String(a||'').trim(),''];
}
function mapRow(row) {
  const addr = (row['Straße']||row['Strasse']||'').toString().trim();
  const [str,nr] = /\d/.test(addr) ? splitStr(addr) : [addr,(row['Hausnummer']||'').toString()];
  const n=[];
  for (const k of ['Bemerkung','Notiz','Pflegegrad','Betreuung_bis']) {
    const v=(row[k]||'').toString().trim();
    if(v&&v!=='0') n.push(k+': '+v);
  }
  return {
    vorname:(row['Vorname']||'').toString().trim(),
    nachname:(row['Name']||row['Nachname']||'').toString().trim(),
    geburtsdatum:parseDatum(row['Geburtsdatum']),
    geschlecht:parseG(row['Geschlecht']),
    strasse:str, hausnummer:nr,
    plz:(row['PLZ']||'').toString().trim(),
    ort:(row['Ort']||'').toString().trim(),
    telefon:(row['Telefon']||row['Tel.']||'').toString().trim(),
    aktenzeichen:(row['Aktenzeichen']||row['Az.']||'').toString().trim(),
    amtsgericht:(row['Amtsgericht']||'').toString().trim(),
    betreuer:(row['Betreuer']||'').toString().trim(),
    aufgabenkreise:(row['Aufgabenkreise']||'').toString().split(/[;,|]/).map(x=>x.trim()).filter(Boolean),
    notizen:n.join('\n'),
  };
}

export default function Butler21ImportTab() {
  /* ─── Excel Import State ─── */
  const [data,setData]=useState([]);
  const [filename,setFilename]=useState('');
  const [importing,setImporting]=useState(false);
  const [results,setResults]=useState(null);
  const [progress,setProgress]=useState(0);

  /* ─── Dokumenten-Pipeline State ─── */
  const [pipelineFiles,setPipelineFiles]=useState([]);
  const [pipelineRunning,setPipelineRunning]=useState(false);
  const [pipelineResults,setPipelineResults]=useState(null);
  const [pipelineProgress,setPipelineProgress]=useState(0);
  const [batchJobId,setBatchJobId]=useState(null);
  const [klientIdHint,setKlientIdHint]=useState('');
  const [klienten,setKlienten]=useState([]);

  /* ─── KI-Kontext State ─── */
  const [kiRunning,setKiRunning]=useState(false);
  const [kiResult,setKiResult]=useState(null);
  const [kontextRunning,setKontextRunning]=useState(false);

  const fileRef=useRef();
  const pipelineRef=useRef();

  /* ─── Butler21 Excel Import ─── */
  function handleExcelFile(file) {
    if(!file) return;
    setFilename(file.name); setResults(null); setKiResult(null);
    const reader=new FileReader();
    reader.onload=e=>{
      try {
        const wb=XLSX.read(new Uint8Array(e.target.result),{type:'array'});
        const rows=XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]],{defval:''});
        setData(rows);
        toast.success(rows.length+' Datensätze geladen');
      } catch(err){ toast.error('Fehler: '+err.message); }
    };
    reader.readAsArrayBuffer(file);
  }

  async function startExcelImport() {
    if(!data.length||importing) return;
    setImporting(true); setProgress(0);
    const mapped=data.map(mapRow).filter(k=>k.nachname);
    const ok=[],fehler=[];
    for(let i=0;i<mapped.length;i+=20){
      const batch=mapped.slice(i,i+20);
      try {
        const r=await fetch(`${API}/api/butler21/import`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(batch)});
        const res=await r.json();
        ok.push(...(res.ok||[])); fehler.push(...(res.fehler||[]));
      } catch(e){ batch.forEach(k=>fehler.push({name:k.vorname+' '+k.nachname,grund:'Fehler'})); }
      setProgress(Math.round(Math.min(i+20,mapped.length)/mapped.length*100));
    }
    setResults({ok,fehler}); setImporting(false);
    toast.success(`${ok.length} importiert – Kontext.md automatisch erstellt`);
  }

  /* ─── Dokumenten-Pipeline ─── */
  function handlePipelineFiles(files) {
    const arr = Array.from(files);
    setPipelineFiles(arr);
    setPipelineResults(null);
    toast.success(`${arr.length} Dateien ausgewählt`);
  }

  async function startPipeline(useKi = false) {
    if(!pipelineFiles.length||pipelineRunning) return;
    setPipelineRunning(true); setPipelineProgress(0); setPipelineResults(null);

    const gesamt = pipelineFiles.length;

    if (useKi && gesamt > 50) {
      const ok = window.confirm(
        `WARNUNG: ${gesamt} Dateien mit KI-Analyse (Claude Vision).\n` +
        `Geschätzte Kosten: ~$${(gesamt * 0.02).toFixed(2)} USD.\n\n` +
        `Fortfahren?`
      );
      if (!ok) { setPipelineRunning(false); return; }
    }

    // Bei < 50 Dateien: synchron, sonst batch
    if (gesamt <= 50) {
      const formData = new FormData();
      pipelineFiles.forEach(f => formData.append('dateien', f));
      if (klientIdHint) formData.append('klient_id', klientIdHint);
      formData.append('use_ki', useKi.toString());

      try {
        const r = await fetch(`${API}/api/pipeline/upload`, { method: 'POST', body: formData });
        const res = await r.json();
        setPipelineResults(res);
        setPipelineProgress(100);
        toast.success(`${res.eingeordnet}/${res.gesamt} Dokumente eingeordnet`);
      } catch(e) {
        toast.error('Pipeline Fehler: ' + e.message);
      }
    } else {
      // Batch: Chunks von 30 Dateien uploaden
      const chunkSize = 30;
      let allResults = { gesamt: 0, eingeordnet: 0, nicht_erkannt: 0, stufe3_noetig: 0, ergebnisse: [] };

      for (let i = 0; i < gesamt; i += chunkSize) {
        const chunk = pipelineFiles.slice(i, i + chunkSize);
        const formData = new FormData();
        chunk.forEach(f => formData.append('dateien', f));
        if (klientIdHint) formData.append('klient_id', klientIdHint);
        formData.append('use_ki', useKi.toString());

        try {
          const r = await fetch(`${API}/api/pipeline/upload`, { method: 'POST', body: formData });
          const res = await r.json();
          allResults.gesamt += res.gesamt;
          allResults.eingeordnet += res.eingeordnet;
          allResults.nicht_erkannt += res.nicht_erkannt;
          allResults.stufe3_noetig += (res.stufe3_noetig || 0);
          allResults.ergebnisse.push(...(res.ergebnisse || []));
        } catch(e) {
          allResults.gesamt += chunk.length;
        }
        setPipelineProgress(Math.round(Math.min(i + chunkSize, gesamt) / gesamt * 100));
      }
      setPipelineResults(allResults);
      toast.success(`${allResults.eingeordnet}/${allResults.gesamt} Dokumente eingeordnet`);
    }
    setPipelineRunning(false);
  }

  /* ─── KI-Kontext ─── */
  async function startKiKontext(ids = null) {
    setKiRunning(true); setKiResult(null);
    try {
      const r = await fetch(`${API}/api/butler21/ki-kontext`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(ids)
      });
      const res = await r.json();
      if (res.fehler && typeof res.fehler === 'string') toast.error(res.fehler);
      else { setKiResult(res); toast.success(`${res.aktualisiert} Kontext-Dateien mit KI angereichert`); }
    } catch(e) { toast.error('Fehler: ' + e.message); }
    setKiRunning(false);
  }

  async function kontextAktualisieren() {
    setKontextRunning(true);
    try {
      const r = await fetch(`${API}/api/butler21/kontext-generieren`, {
        method: 'POST', headers: {'Content-Type': 'application/json'}, body: 'null'
      });
      const res = await r.json();
      toast.success(`${res.aktualisiert} Kontext-Dateien aktualisiert`);
    } catch(e) { toast.error('Fehler: ' + e.message); }
    setKontextRunning(false);
  }

  return (
    <div className="space-y-4">

      {/* ═══ DOKUMENTEN-PIPELINE ═══ */}
      <Card data-testid="pipeline-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5"/>Dokumenten-Pipeline (Hephaistion)
          </CardTitle>
          <CardDescription>
            Alle Dateien hochladen – PDF, Bilder, Word, Excel, etc. Automatische OCR, Zuordnung und Einsortierung.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            data-testid="pipeline-dropzone"
            className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={()=>pipelineRef.current?.click()}
            onDragOver={e=>e.preventDefault()}
            onDrop={e=>{e.preventDefault();handlePipelineFiles(e.dataTransfer.files);}}>
            <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground"/>
            {pipelineFiles.length > 0
              ? <p className="font-medium">{pipelineFiles.length} Dateien ausgewählt
                  <span className="text-xs text-muted-foreground ml-2">
                    ({(pipelineFiles.reduce((s,f)=>s+f.size,0)/1024/1024).toFixed(1)} MB)
                  </span>
                </p>
              : <div>
                  <p className="text-muted-foreground">Dokumente hier ablegen oder klicken</p>
                  <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG, DOCX, XLSX, TXT — bis zu 1200+ Dateien</p>
                </div>
            }
          </div>
          <input ref={pipelineRef} type="file" multiple accept="*/*" className="hidden"
            onChange={e=>handlePipelineFiles(e.target.files)}/>

          {/* Klient-Zuordnung (optional) */}
          {pipelineFiles.length > 0 && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium block mb-1">Optional: Betreuter zuordnen (wenn bekannt)</label>
                <input
                  data-testid="pipeline-klient-hint"
                  type="text"
                  placeholder="Klient-ID eingeben oder leer lassen für Auto-Erkennung"
                  value={klientIdHint}
                  onChange={e => setKlientIdHint(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  data-testid="pipeline-start-btn"
                  onClick={() => startPipeline(false)}
                  disabled={pipelineRunning}
                  className="w-full"
                >
                  {pipelineRunning
                    ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Verarbeite... {pipelineProgress}%</span>
                    : <span className="flex items-center gap-2"><FileText className="w-4 h-4"/>Stufe 1+2: OCR + Auto-Zuordnung</span>
                  }
                </Button>
                <Button
                  data-testid="pipeline-ki-btn"
                  onClick={() => startPipeline(true)}
                  disabled={pipelineRunning}
                  variant="outline"
                  className="w-full border-purple-200 hover:bg-purple-50"
                >
                  <span className="flex items-center gap-2"><Brain className="w-4 h-4 text-purple-600"/>+ Stufe 3: Claude Vision</span>
                </Button>
              </div>

              {pipelineRunning && (
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full transition-all" style={{width:pipelineProgress+'%'}}/>
                </div>
              )}
            </div>
          )}

          {/* Pipeline Ergebnisse */}
          {pipelineResults && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-green-200 bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-semibold text-green-700">{pipelineResults.eingeordnet}</p>
                  <p className="text-xs text-green-600">Eingeordnet</p>
                </div>
                <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-semibold text-yellow-700">{pipelineResults.nicht_erkannt}</p>
                  <p className="text-xs text-yellow-600">Nicht erkannt</p>
                </div>
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-semibold text-blue-700">{pipelineResults.gesamt}</p>
                  <p className="text-xs text-blue-600">Gesamt</p>
                </div>
              </div>

              {pipelineResults.stufe3_noetig > 0 && (
                <div className="flex items-center gap-2 p-3 border border-amber-200 bg-amber-50 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0"/>
                  <p className="text-sm text-amber-800">
                    <strong>{pipelineResults.stufe3_noetig} Dokumente</strong> konnten mit Stufe 1+2 nicht erkannt werden.
                    Claude Vision (Stufe 3) könnte helfen (~${(pipelineResults.stufe3_noetig * 0.02).toFixed(2)} USD).
                  </p>
                </div>
              )}

              {/* Ergebnis-Liste */}
              {pipelineResults.ergebnisse?.length > 0 && (
                <div className="text-xs border rounded overflow-hidden max-h-64 overflow-y-auto">
                  <div className="grid grid-cols-4 gap-2 px-3 py-2 bg-muted font-medium sticky top-0">
                    <span>Datei</span><span>Klient</span><span>Methode</span><span>Status</span>
                  </div>
                  {pipelineResults.ergebnisse.map((r,i)=>(
                    <div key={i} className="grid grid-cols-4 gap-2 px-3 py-1.5 border-t items-center">
                      <span className="truncate" title={r.dateiname}>{r.dateiname}</span>
                      <span className="truncate">{r.klient_name || '-'}</span>
                      <span className="text-muted-foreground">{r.methode}</span>
                      <span>{r.eingeordnet
                        ? <CheckCircle className="w-4 h-4 text-green-600 inline"/>
                        : <XCircle className="w-4 h-4 text-red-400 inline"/>
                      }</span>
                    </div>
                  ))}
                </div>
              )}

              <Button variant="outline" onClick={()=>{setPipelineFiles([]);setPipelineResults(null);}} className="w-full">
                Weitere Dokumente verarbeiten
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ BUTLER21 EXCEL IMPORT ═══ */}
      <Card data-testid="butler21-import-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5"/>Butler21 Migration (Excel)
          </CardTitle>
          <CardDescription>Klienten-Liste aus Butler21: Datei - Export - Betreute Liste (.xlsx/.csv)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            data-testid="butler21-dropzone"
            className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={()=>fileRef.current?.click()}
            onDragOver={e=>e.preventDefault()}
            onDrop={e=>{e.preventDefault();handleExcelFile(e.dataTransfer.files[0]);}}>
            <FileSpreadsheet className="w-6 h-6 mx-auto mb-2 text-muted-foreground"/>
            {filename
              ? <p className="font-medium">{filename} <span className="text-green-600">— {data.length} Datensätze</span></p>
              : <p className="text-muted-foreground text-sm">Butler21-Export (.xlsx / .csv) hier ablegen</p>
            }
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
            onChange={e=>handleExcelFile(e.target.files[0])}/>

          {data.length>0&&!results&&(
            <Button data-testid="butler21-start-import" onClick={startExcelImport} disabled={importing} className="w-full">
              {importing
                ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Importiere... {progress}%</span>
                : <span className="flex items-center gap-2"><Upload className="w-4 h-4"/>{data.length} Klienten importieren</span>
              }
            </Button>
          )}
          {importing&&<div className="w-full bg-muted rounded-full h-2"><div className="bg-primary h-2 rounded-full transition-all" style={{width:progress+'%'}}/></div>}

          {results&&(
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-green-200 bg-green-50 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600"/>
                  <div><p className="text-xl font-semibold text-green-700">{results.ok.length}</p><p className="text-xs text-green-600">Importiert</p></div>
                </div>
                <div className="border border-red-200 bg-red-50 rounded-lg p-3 flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-600"/>
                  <div><p className="text-xl font-semibold text-red-700">{results.fehler.length}</p><p className="text-xs text-red-600">Fehler</p></div>
                </div>
              </div>
              {results.ok.length > 0 && (
                <Button data-testid="butler21-ki-kontext-btn" onClick={() => startKiKontext(results.ok.map(k=>k.id))} disabled={kiRunning} variant="outline" className="w-full border-purple-200 hover:bg-purple-50">
                  {kiRunning ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>KI analysiert...</span>
                    : <span className="flex items-center gap-2"><Brain className="w-4 h-4 text-purple-600"/>KI-Kontext für {results.ok.length} Klienten</span>}
                </Button>
              )}
              {kiResult && <div className="border border-purple-200 bg-purple-50 rounded-lg p-3"><p className="text-sm font-medium text-purple-800">KI: {kiResult.aktualisiert} Kontext-Dateien angereichert</p></div>}
              <Button variant="outline" onClick={()=>{setData([]);setFilename('');setResults(null);setKiResult(null);}} className="w-full">Neuer Import</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ KONTEXT-VERWALTUNG ═══ */}
      <Card data-testid="kontext-verwaltung-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Brain className="w-5 h-5 text-purple-600"/>Kontext-Verwaltung</CardTitle>
          <CardDescription>Kontext.md für alle Betreuungsakten verwalten</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button data-testid="kontext-aktualisieren-btn" onClick={kontextAktualisieren} disabled={kontextRunning} variant="outline" className="h-auto py-3">
              <div className="flex items-center gap-2">
                {kontextRunning ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
                <div className="text-left"><p className="font-medium">Kontext aktualisieren</p><p className="text-xs text-muted-foreground font-normal">Stammdaten aus DB</p></div>
              </div>
            </Button>
            <Button data-testid="ki-kontext-alle-btn" onClick={()=>startKiKontext(null)} disabled={kiRunning} variant="outline" className="h-auto py-3 border-purple-200 hover:bg-purple-50">
              <div className="flex items-center gap-2">
                {kiRunning ? <Loader2 className="w-4 h-4 animate-spin"/> : <Brain className="w-4 h-4 text-purple-600"/>}
                <div className="text-left"><p className="font-medium">KI-Kontext generieren</p><p className="text-xs text-muted-foreground font-normal">Claude analysiert alle Fälle</p></div>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
